import SportEntityDto from "./SportEntityDto";

export interface QuizConfigurationDto {
    id: number;
    sport: SportEntityDto | null;
    leagueIds: number[];
    betGroupIds: number[];
    deadlineInHoursBeforeMatchStart: number;
    isActive: boolean;
}

export interface PaginatedQuizConfigurationListDto {
    items: QuizConfigurationDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

export interface SaveQuizConfigurationRequest {
    id?: number | null;
    sportId: number;
    leagueIds: number[];
    betGroupIds: number[];
    deadlineInHoursBeforeMatchStart: number;
    isActive: boolean;
}

export interface QuizEligibleMatchDto {
    id: number;
    startDate: string | null;
    leagueName: string | null;
    homeTeamName: string | null;
    awayTeamName: string | null;
}

export interface PaginatedQuizEligibleMatchesDto {
    items: QuizEligibleMatchDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

export interface QuizTierDto {
    id: number;
    fee: number;
    oddsBoost: number;
    name: string | null;
    nameRu: string | null;
    imgUrl: string | null;
}

export interface SaveQuizTierRequest {
    id?: number | null;
    fee: number;
    oddsBoost: number;
    name: string | null;
    nameRu?: string | null;
    imgUrl?: string | null;
}

export interface QuizBetTypeGroupOptionDto {
    id: number;
    name: string | null;
    betTypeGroupId: number | null;
    betTypeGroupName: string | null;
}

export interface QuizBetTypeDto {
    id: number;
    betTypeId: number | null;
    betTypeName: string | null;
    quizBetTypeGroupId: number | null;
    quizBetTypeGroupName: string | null;
    name: string | null;
    nameRu: string | null;
}

export interface SaveQuizBetTypeRequest {
    id?: number | null;
    betTypeId: number;
    quizBetTypeGroupId: number;
    name: string | null;
    nameRu?: string | null;
    nameRo?: string | null;
    nameFr?: string | null;
    nameTr?: string | null;
    nameIt?: string | null;
    nameDe?: string | null;
    nameEs?: string | null;
    namePt?: string | null;
}

export interface QuizParticipationCalculationDto {
    settled: boolean;
    savedByCron: boolean;
    expectedState: number | null;
    expectedMoneyReturned: number | null;
    expectedCoefficient: number | null;
    stateMatches: boolean;
    moneyMatches: boolean;
    coefficientMatches: boolean;
    matchesStored: boolean;
    reviewNeeded: boolean;
    unsettledAnswers: boolean;
    summary: string;
    warnings: string[];
}

export interface QuizParticipationAnswerDto {
    placedQuizBetId: number;
    quizBetId: number;
    questionNumber: number;
    optionNumber: number;
    coefficient: number;
    betTypeParam: number;
    groupName: string | null;
    betTypeName: string | null;
    placedBetId: number | null;
    placedBetState: number | null;
    betName: string | null;
    marketBetTypeName: string | null;
    placedOdds: number | null;
    currentOdds: number | null;
    currentOddsAvailable: boolean | null;
    varietyScoreHome: number | null;
    varietyScoreAway: number | null;
    varietyFinished: boolean;
    varietyCancelled: boolean;
}

export interface QuizParticipationAdminItemDto {
    id: number;
    createdAt: string | null;
    state: number;
    moneyPlaced: number;
    moneyReturned: number;
    totalCoefficient: number;
    quizId: number;
    quizState: number;
    matchId: number | null;
    homeTeamName: string | null;
    awayTeamName: string | null;
    matchStartDate: string | null;
    matchState: number | null;
    scoreHome: number | null;
    scoreAway: number | null;
    tierId: number | null;
    tierName: string | null;
    oddsBoost: number;
    tierFee: number;
    userId: number;
    userName: string | null;
    answerCount: number;
    wonAnswers: number;
    lostAnswers: number;
    pendingAnswers: number;
    returnedAnswers: number;
    calculation: QuizParticipationCalculationDto;
}

export interface QuizParticipationAdminDetailDto {
    participation: QuizParticipationAdminItemDto;
    answers: QuizParticipationAnswerDto[];
}

export interface PaginatedQuizParticipationsDto {
    items: QuizParticipationAdminItemDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

export const QUIZ_PARTICIPATION_STATE_OPTIONS = [
    { value: 2, label: 'Pending' },
    { value: 4, label: 'Won' },
    { value: 8, label: 'Lost' },
    { value: 1, label: 'Returned' },
    { value: 32, label: 'Cancelled' },
];

export const QUIZ_STATE_OPTIONS = [
    { value: 1, label: 'Open' },
    { value: 2, label: 'Closed' },
    { value: 3, label: 'Finished' },
    { value: 4, label: 'Cancelled' },
];

export function quizParticipationStateLabel(state: number | null | undefined): string {
    switch (state) {
        case 1: return 'Returned';
        case 2: return 'Pending';
        case 4: return 'Won';
        case 8: return 'Lost';
        case 32: return 'Cancelled';
        default: return state == null ? '—' : `Unknown (${state})`;
    }
}

export function quizStateLabel(state: number | null | undefined): string {
    switch (state) {
        case 1: return 'Open';
        case 2: return 'Closed';
        case 3: return 'Finished';
        case 4: return 'Cancelled';
        default: return state == null ? '—' : `Unknown (${state})`;
    }
}

export function matchStateLabel(state: number | null | undefined): string {
    switch (state) {
        case 1: return 'Future';
        case 2: return 'Live';
        case 3: return 'Finished';
        case 4: return 'Processing';
        case 5: return 'Cancelled';
        default: return state == null ? '—' : `Unknown (${state})`;
    }
}
